import axios from 'axios';
import { toast } from 'react-toastify';
import { BASE_URL } from '../constants/Constants';

const axiosInstance = axios.create();

// Request interceptor — attach token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    const provider = localStorage.getItem('auth_provider');
    if (token && provider) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['x-auth-provider'] = provider;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = []; // queue requests that arrived while refresh was in progress

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const clearSessionAndRedirect = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('auth_provider');
  window.location.href = '/';
};

// Response interceptor — handle 401 with token refresh + retry
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');
      const provider = localStorage.getItem('auth_provider');

      if (!refreshToken) {
        clearSessionAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue this request until the refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }).catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        let newAccessToken;

        if (provider === 'google') {
          const res = await axios.post(`${BASE_URL}/auth/refresh-token`, { refresh_token: refreshToken });
          newAccessToken = res.data.id_token;
          if (res.data.refresh_token) {
            localStorage.setItem('refresh_token', res.data.refresh_token);
          }
        } else if (provider === 'mojoauth') {
          const res = await axios.post(`${BASE_URL}/auth/mojoauth/refresh`, { refresh_token: refreshToken });
          newAccessToken = res.data.access_token;
        } else {
          throw new Error('Unknown provider');
        }

        localStorage.setItem('access_token', newAccessToken);
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearSessionAndRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status !== 401) {
      toast.error('Something went wrong!');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;